import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';

import Autocomplete from 'components/autocomplete';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Plus } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import { DefaultValues, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import titleCase from 'utils/title-case';
import { getGenerations } from 'views/bluviu-nutrition/features/scenarios-v2/api/generations-get';
import { createScenario } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-create';
import { CreateScenarioInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/create-scenario-input.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { ScenarioGeneration } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-generation.type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { BluViuNutritionParams } from 'views/bluviu-nutrition/types/bluviu-nutrition-params.type';
import { addDatadogRumAction } from 'utils/datadog-rum';

const PAGE_NAME_ID = 'bluViuNutrition.listScenarios';

type Props = {};

interface CreateScenarioForm {
  name: string;
  generation: ScenarioGeneration;
}

const ScenarioCreateDialog: React.FC<Props> = ({ }) => {
  const {
    account: { company },
  } = useAccountState();
  const { selectedSiteId, selectedSite } = useSelectedSite();
  const history = useHistory();
  const params = useParams<BluViuNutritionParams>();
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const {
    r2ScenariosQuery: { data: scenarios },
  } = useR2ScenarioState();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const { data: generations, isLoading: generationsIsLoading } = useGet(['getGenerations'], () => getGenerations());

  const invalidNames = useMemo(() => scenarios?.map((scenario) => scenario.name) ?? [], [scenarios]);

  const defaultValues: DefaultValues<CreateScenarioForm> = useMemo(
    () => ({
      name: '',
    }),
    []
  );

  const postScenario = usePost<string, { globalCompanyId: string; globalSiteId: string; data: CreateScenarioInput }>(
    ({ globalCompanyId, globalSiteId, data }) => createScenario(globalCompanyId, globalSiteId, data),
    (scenarioId) => {
      addDatadogRumAction('scenario_created', {
        scenario_model: ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI,
        site_id: selectedSiteId,
        has_generation: true,
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenarios', selectedSiteId]
      });
      dialogRef.current?.setOpen(false);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      history.push(`/bluviu-nutrition/r2/${params.globalSiteId}/${scenarioId}/setup`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const { handleSubmit, control, reset } = useForm<CreateScenarioForm>({
    defaultValues: defaultValues,
    shouldUnregister: true,
  });

  const onSubmit = (data: CreateScenarioForm) => {
    postScenario.mutate({
      globalCompanyId: company!.companyId,
      globalSiteId: selectedSiteId!,
      data: {
        name: data.name,
        generationYear: data.generation.year,
        generationSeason: data.generation.season,
      },
    });
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'sm'}
      title={`${intl.formatMessage({ id: `${PAGE_NAME_ID}.createDialog.title` })}`}
      button={
        <Button startIcon={<Plus />}>
          <FormattedMessage id={`${PAGE_NAME_ID}.createDialog.title`} />
        </Button>
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>For site: {selectedSite?.name}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <TextField
              dark
              fullWidth
              controller={{
                control,
                name: 'name',
                rules: {
                  required: true,
                  validate: (value) => (!invalidNames.includes(value) ? true : `Name must not match an existing scenario.`),
                },
              }}
              label={'Scenario name'}
            />
            <Box display={'flex'} gap={4}>
              <Autocomplete
                dark
                label={'Generation'}
                disabled={generationsIsLoading}
                disableCloseOnSelect={false}
                options={generations ?? []}
                getOptionLabel={(option) => (option as ScenarioGeneration).displayName}
                controller={{
                  control,
                  name: 'generation',
                  rules: {
                    required: true,
                    validate: (value) => (!invalidNames.includes(value) ? true : `Please add a generation`),
                  },
                }}
              />
              <TextField fullWidth dark label={'Model'} disabled value={titleCase(ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI)} />
            </Box>

            <Box display={'flex'} justifyContent={'end'}>
              <Button
                type="submit"
                disabled={postScenario.isPending}
                startIcon={postScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                <FormattedMessage id={`general.create`} values={{ type: '' }} />
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(ScenarioCreateDialog);
